/**
 * ImaginLabs shared carousel engine.
 * One engine, reused by: announcement bar, hero slider, product carousels
 * (bestsellers / new arrivals / upcoming), and the Watch-It-Love-It peek carousel.
 *
 * Markup contract:
 *   <div data-ih-carousel>              root
 *     <div data-ih-track>               track (flex row, transformed)
 *       <div data-ih-slide>...</div>    one per slide
 *       ...
 *     </div>
 *   </div>
 *   optional: [data-ih-prev], [data-ih-next], [data-ih-dots]
 *
 * Safe to include multiple times on a page; each instance is independent.
 */
(function () {
  if (window.IHCarousel) return;

  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  function IHCarousel(root, opts) {
    if (!root || root.__ihCarousel) return root && root.__ihCarousel;
    opts = opts || {};
    this.root = root;
    this.track = root.querySelector('[data-ih-track]');
    this.slides = this.track ? Array.prototype.slice.call(this.track.querySelectorAll(':scope > [data-ih-slide]')) : [];
    this.prevBtn = opts.prevBtn || root.querySelector('[data-ih-prev]');
    this.nextBtn = opts.nextBtn || root.querySelector('[data-ih-next]');
    this.dotsWrap = opts.dotsWrap || root.querySelector('[data-ih-dots]');

    this.getPerView = typeof opts.perView === 'function' ? opts.perView : function () { return opts.perView || 1; };
    this.slidesToMove = opts.slidesToMove || null; // null = one page at a time
    this.loop = opts.loop !== false;
    this.transition = opts.transition || 'slide'; // slide-left | slide-right | slide-up | slide-down | fade | fade-slide | none
    this.duration = opts.duration || 450; // ms
    this.autoplay = !!opts.autoplay;
    this.autoplaySpeed = opts.autoplaySpeed || 4000;
    this.pauseOnHover = opts.pauseOnHover !== false;
    this.swipe = opts.swipe !== false;
    this.onChange = opts.onChange || function () {};
    this.peek = !!opts.peek; // continuous center-peek mode (Watch It Love It)

    this.page = 0;
    this._timer = null;
    this._dragStartX = null;
    this._dragDelta = 0;

    this._buildDots();
    this._bindArrows();
    if (this.swipe) this._bindSwipe();
    this._bindHover();
    this.goTo(0, { instant: true });
    this._startAutoplay();

    var self = this;
    window.addEventListener('resize', function () {
      clearTimeout(self._resizeT);
      self._resizeT = setTimeout(function () { self.goTo(self.page, { instant: true }); }, 120);
    });

    root.__ihCarousel = this;
  }

  IHCarousel.prototype.pageCount = function () {
    var per = Math.max(1, this.getPerView());
    var step = this.slidesToMove || per;
    return Math.max(1, Math.ceil(Math.max(0, this.slides.length - per) / step) + 1);
  };

  IHCarousel.prototype._buildDots = function () {
    if (!this.dotsWrap) return;
    var pages = this.pageCount();
    this.dotsWrap.innerHTML = '';
    this.dotsWrap.setAttribute('role', 'tablist');
    if (pages <= 1) { this.dotsWrap.style.display = 'none'; return; }
    this.dotsWrap.style.display = '';
    var self = this;
    for (var i = 0; i < pages; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ih-car-dot';
      b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      (function (idx) {
        b.addEventListener('click', function () { self.goTo(idx); self._restartAutoplay(); });
      })(i);
      this.dotsWrap.appendChild(b);
    }
    this._syncDots();
  };

  IHCarousel.prototype._syncDots = function () {
    if (!this.dotsWrap) return;
    var dots = this.dotsWrap.querySelectorAll('.ih-car-dot');
    dots.forEach(function (d, i) { d.classList.toggle('is-active', i === this.page); }, this);
  };

  IHCarousel.prototype._syncArrows = function () {
    var pages = this.pageCount();
    if (this.prevBtn) this.prevBtn.classList.toggle('is-disabled', !this.loop && this.page <= 0);
    if (this.nextBtn) this.nextBtn.classList.toggle('is-disabled', !this.loop && this.page >= pages - 1);
  };

  IHCarousel.prototype._bindArrows = function () {
    var self = this;
    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.prev(); self._restartAutoplay(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.next(); self._restartAutoplay(); });
  };

  IHCarousel.prototype._bindHover = function () {
    if (!this.pauseOnHover) return;
    var self = this;
    this.root.addEventListener('mouseenter', function () { self._stopAutoplay(); });
    this.root.addEventListener('mouseleave', function () { self._startAutoplay(); });
  };

  IHCarousel.prototype._bindSwipe = function () {
    var self = this;
    var startX = 0, startY = 0, dragging = false, axisLocked = null;

    function down(e) {
      dragging = true; axisLocked = null;
      var p = e.touches ? e.touches[0] : e;
      startX = p.clientX; startY = p.clientY;
      self.track.style.transition = 'none';
    }
    function move(e) {
      if (!dragging) return;
      var p = e.touches ? e.touches[0] : e;
      var dx = p.clientX - startX, dy = p.clientY - startY;
      if (axisLocked === null) axisLocked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (axisLocked === 'y') return; // let vertical page scroll happen
      if (e.cancelable) e.preventDefault();
      self._dragDelta = dx;
      self._applyTransform(self._baseOffsetPct() , dx);
    }
    function up() {
      if (!dragging) return;
      dragging = false;
      self.track.style.transition = '';
      var threshold = 40;
      if (self._dragDelta > threshold) self.prev();
      else if (self._dragDelta < -threshold) self.next();
      else self.goTo(self.page);
      self._dragDelta = 0;
      self._restartAutoplay();
    }

    this.root.addEventListener('touchstart', down, { passive: true });
    this.root.addEventListener('touchmove', move, { passive: false });
    this.root.addEventListener('touchend', up);
    this.root.addEventListener('mousedown', down);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  IHCarousel.prototype._baseOffsetPct = function (forPage) {
    var per = Math.max(1, this.getPerView());
    var step = this.slidesToMove || per;
    var slideW = this.track.clientWidth / per;
    var page = (forPage === undefined) ? this.page : forPage;
    return -(page * step * slideW);
  };

  IHCarousel.prototype._applyTransform = function (basePx, dragPx) {
    if (this.transition === 'none') return;
    var extra = dragPx || 0;
    this.track.style.transform = 'translateX(' + (basePx + extra) + 'px)';
  };

  IHCarousel.prototype.next = function () { this.goTo(this.page + 1, { dir: 1 }); };
  IHCarousel.prototype.prev = function () { this.goTo(this.page - 1, { dir: -1 }); };

  IHCarousel.prototype.goTo = function (page, opts) {
    opts = opts || {};
    var pages = this.pageCount();
    var oldPage = this.page;
    if (this.loop) {
      page = ((page % pages) + pages) % pages;
    } else {
      page = clamp(page, 0, pages - 1);
    }
    this.page = page;

    if (this.track) {
      var t = this.transition;
      var self = this;

      if (t === 'none' || opts.instant) {
        this.track.style.transition = 'none';
        this.track.style.opacity = '1';
        this._applyTransform(this._baseOffsetPct(), 0);
      } else if (t === 'fade') {
        // Jump-cut fade: fade out, reposition while invisible, fade in.
        this.track.style.transition = 'opacity ' + (this.duration * 0.5) + 'ms ease';
        this.track.style.opacity = '0';
        setTimeout(function () {
          self.track.style.transition = 'none';
          self._applyTransform(self._baseOffsetPct(), 0);
          requestAnimationFrame(function () {
            self.track.style.transition = 'opacity ' + (self.duration * 0.5) + 'ms ease';
            self.track.style.opacity = '1';
          });
        }, this.duration * 0.5);
      } else if (t === 'fade-slide') {
        // Slide and fade simultaneously — no jump cut.
        this.track.style.transition = 'transform ' + this.duration + 'ms ease, opacity ' + this.duration + 'ms ease';
        this.track.style.opacity = '0.15';
        this._applyTransform(this._baseOffsetPct(), 0);
        requestAnimationFrame(function () { self.track.style.opacity = '1'; });
      } else if (t === 'slide-up' || t === 'slide-down') {
        // Jump-cut vertical reveal: exit old page upward/downward, snap horizontally
        // to the new page while off-screen, then enter from the opposite side.
        var per2 = Math.max(1, this.getPerView());
        var h = (this.track.clientHeight / per2) || this.root.clientHeight || 34;
        var dirY = t === 'slide-up' ? -1 : 1;
        var oldX = this._baseOffsetPct(oldPage);
        var newX = this._baseOffsetPct(this.page);
        this.track.style.transition = 'transform ' + (this.duration * 0.5) + 'ms ease';
        this.track.style.opacity = '1';
        this.track.style.transform = 'translate(' + oldX + 'px,' + (dirY * h) + 'px)';
        setTimeout(function () {
          self.track.style.transition = 'none';
          self.track.style.transform = 'translate(' + newX + 'px,' + (-dirY * h) + 'px)';
          requestAnimationFrame(function () {
            self.track.style.transition = 'transform ' + (self.duration * 0.5) + 'ms ease';
            self.track.style.transform = 'translate(' + newX + 'px,0px)';
          });
        }, this.duration * 0.5);
      } else if (t === 'slide-right') {
        // Same mechanics as slide-left, mirrored direction.
        this.track.style.transition = 'transform ' + this.duration + 'ms cubic-bezier(.22,.61,.36,1)';
        this.track.style.opacity = '1';
        this._applyTransform(-this._baseOffsetPct(), 0);
      } else {
        // slide-left (default)
        this.track.style.transition = 'transform ' + this.duration + 'ms cubic-bezier(.22,.61,.36,1)';
        this.track.style.opacity = '1';
        this._applyTransform(this._baseOffsetPct(), 0);
      }
    }

    this._syncDots();
    this._syncArrows();
    this.onChange(this.page, pages);
  };

  IHCarousel.prototype._startAutoplay = function () {
    if (!this.autoplay) return;
    this._stopAutoplay();
    var self = this;
    this._timer = setInterval(function () {
      if (!self.loop && self.page >= self.pageCount() - 1) { self.goTo(0); return; }
      self.next();
    }, this.autoplaySpeed);
  };
  IHCarousel.prototype._stopAutoplay = function () { if (this._timer) { clearInterval(this._timer); this._timer = null; } };
  IHCarousel.prototype._restartAutoplay = function () { this._stopAutoplay(); this._startAutoplay(); };

  IHCarousel.prototype.destroy = function () {
    this._stopAutoplay();
    this.root.__ihCarousel = null;
  };

  window.IHCarousel = IHCarousel;

  // Re-init any [data-ih-carousel] that declares its config via data-attributes / JSON,
  // and re-run automatically when a section re-renders in the theme editor.
  window.ihInitCarousels = function (scope) {
    (scope || document).querySelectorAll('[data-ih-carousel-auto]').forEach(function (root) {
      if (root.__ihCarousel) root.__ihCarousel.destroy();
      var cfg = {};
      try { cfg = JSON.parse(root.getAttribute('data-ih-carousel-auto') || '{}'); } catch (e) {}
      new IHCarousel(root, cfg);
    });
  };

  document.addEventListener('DOMContentLoaded', function () { window.ihInitCarousels(); });
  document.addEventListener('shopify:section:load', function (evt) {
    if (evt.target) window.ihInitCarousels(evt.target);
  });
})();
